import { Button } from "@mui/material";
import axios from "axios";
import firebase from "firebase/app";
import { useEffect, useState } from "react";
import { useUser } from "reactfire";
import { HKWorkout } from "@converge-collective/common/models/HKWorkout";
import {
  NativeBridgeMessageType,
  NativeBridgeMessageTypes,
} from "@converge-collective/common/models/NativeAppBridge";
import { useRouter } from "next/router";

declare const window: any;
declare const document: any;

export const hasRN = (): boolean => window?.ReactNativeWebView;

const getCustomToken = async (): Promise<string> => {
  const result = await axios.post("/api/custom-token");
  console.log("RNB custom token result", { data: result.data });
  return result.data.customToken;
};

export const postEnableDebug = (): void => {
  if (hasRN()) {
    postToRN(NativeBridgeMessageTypes.EnableDebug);
  }
};

export const postGetWorkouts = (): void => {
  if (hasRN()) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        converge: true,
        messageType: NativeBridgeMessageTypes.GetWorkouts,
      })
    );
  }
};

export type RNWorkout = HKWorkout;

export const useRNWorkouts = (): [RNWorkout[]] => {
  const [workouts, setWorkouts] = useState<RNWorkout[]>([]);
  useEffect(() => {
    // listen for the response from RN
    const onMessage = async (message: MessageEvent) => {
      if (message.data) {
        const data = message.data;
        // ensure the event came from converge
        if (data.converge) {
          console.log("RNB converge MessageEvent", { message });
          // const event = { fromWeb: "Hello from Web!", data };
          if (data.messageType === NativeBridgeMessageTypes.SetWorkouts) {
            setWorkouts(data.workouts);
          }
        }
      }
    };
    window.addEventListener("message", onMessage);

    // ask RN to send us the workouts
    postGetWorkouts();
  }, []);

  return [workouts];
};

// Send a custom Firebase Auth Token to the RN wrapper.
export const postTokenToRN = async (user?: firebase.User): Promise<void> => {
  // if user is null/undefined then post a null user and token, which
  // indicates that RN should log the user out
  const token = user && (await getCustomToken());
  console.log("postTokenToRN", { user, token });
  return postToRN(NativeBridgeMessageTypes.Token, {
    user,
    token,
  });
};

export const postToRN = async (
  messageType: NativeBridgeMessageType,
  extra: Record<string, any> = {}
): Promise<void> => {
  if (!window.ReactNativeWebView) {
    // console.warn("window.ReactNativeWebView is undefined");
    return;
  }
  window.ReactNativeWebView.postMessage(
    JSON.stringify({ messageType, ...extra, converge: true })
  );
};

export default function ReactNativeBridge(): React.ReactElement {
  const { data: user } = useUser();
  const router = useRouter();

  const [isDebugEnabled] = useState(false);
  const [debug, setDebug] = useState("");
  const [token, setToken] = useState("");

  const appendDebug = (text: string) => setDebug(text + "\n\n" + debug);

  const fetchToken = async () => {
    console.log("fetching...");
    const t = await getCustomToken();
    setToken(t);
  };

  useEffect(() => {
    console.count("adding addEventListener for RN");
    const onMessage = async (message: MessageEvent) => {
      const { data } = message;
      // ensure the event came from the converge app
      if (data.converge) {
        appendDebug(
          "message type: " +
            data.messageType +
            "\n" +
            JSON.stringify(message, null, 2)
        );
        console.log("RNB converge MessageEvent", { message });
        // const event = { fromWeb: "Hello from Web!", data };
        if (data.messageType === NativeBridgeMessageTypes.GetAuthStatus) {
          console.log("Getting auth status with user", { user });
          postTokenToRN(user);
        }

        if (data.messageType === NativeBridgeMessageTypes.Nav) {
          // set hash urls manually - nextjs router prevents correct handling of
          // hashes by the `useHash` hook
          if (data.url.startsWith("#")) {
            location.hash = data.url;
          } else {
            router.push(data.url);
          }
        }
      }
    };
    // ios needs the listener on window
    window.addEventListener("message", onMessage);
    // android needs the listener on document
    document.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      document.removeEventListener("message", onMessage);
    };
  }, []);

  return (
    <div>
      {isDebugEnabled && (
        <>
          <Button onClick={fetchToken} variant="contained">
            Fetch Token
          </Button>
          <pre>{token}</pre>
          <pre> {debug} </pre>
        </>
      )}
    </div>
  );
}
